import React, { useState, useEffect } from 'react';
import { Star, Check, Trash2, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminReviews = () => {
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pendingReviews, setPendingReviews] = useState([]);
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchReviews = async () => {
    setLoading(true);
    setError('');
    try {
      const [pendingRes, allRes] = await Promise.all([
        axios.get(`${API}/reviews/pending?password=${encodeURIComponent(password)}`),
        axios.get(`${API}/reviews/all?password=${encodeURIComponent(password)}`)
      ]);
      setPendingReviews(pendingRes.data);
      setApprovedReviews(allRes.data.filter(r => r.approved));
    } catch (err) {
      setError('Failed to fetch reviews. Check your password.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.get(`${API}/reviews/pending?password=${encodeURIComponent(password)}`);
      setIsAuthenticated(true);
      fetchReviews();
    } catch (err) {
      setError('Invalid password');
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId) => {
    try {
      await axios.put(`${API}/reviews/${reviewId}/approve?password=${encodeURIComponent(password)}`);
      fetchReviews();
    } catch (err) {
      console.error('Failed to approve review:', err);
    }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    try {
      await axios.delete(`${API}/reviews/${reviewId}?password=${encodeURIComponent(password)}`);
      fetchReviews();
    } catch (err) {
      console.error('Failed to delete review:', err);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchReviews();
    }
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-4">
        <div className="bg-white border border-neutral-200 rounded-2xl p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-brand/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-brand" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900">Admin Login</h1>
            <p className="text-neutral-600">Enter password to manage reviews</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="bg-white border-neutral-300 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            
            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-brand hover:bg-brand-dark text-white"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-neutral-900">Review Management</h1>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => window.location.href = '/admin/inquiries'}
              className="border-neutral-300"
            >
              View Enquiries
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setIsAuthenticated(false);
                setPassword('');
              }}
              className="border-neutral-300"
            >
              Logout
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'pending' ? 'default' : 'outline'}
            onClick={() => setActiveTab('pending')}
            className={activeTab === 'pending' ? 'bg-brand hover:bg-brand-dark' : 'border-neutral-300'}
          >
            Pending ({pendingReviews.length})
          </Button>
          <Button
            variant={activeTab === 'approved' ? 'default' : 'outline'}
            onClick={() => setActiveTab('approved')}
            className={activeTab === 'approved' ? 'bg-brand hover:bg-brand-dark' : 'border-neutral-300'}
          >
            Approved ({approvedReviews.length})
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <div className="space-y-4">
            {(activeTab === 'pending' ? pendingReviews : approvedReviews).length === 0 ? (
              <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
                <p className="text-neutral-500">
                  {activeTab === 'pending' ? 'No pending reviews' : 'No approved reviews yet'}
                </p>
              </div>
            ) : (
              (activeTab === 'pending' ? pendingReviews : approvedReviews).map((review) => (
                <div
                  key={review.id}
                  className="bg-white border border-neutral-200 rounded-xl p-6"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-neutral-900">{review.name}</span>
                        <span className="text-neutral-400">•</span>
                        <span className="text-neutral-500 text-sm">{review.location}</span>
                        {review.service_type && (
                          <>
                            <span className="text-neutral-400">•</span>
                            <span className="text-brand text-sm capitalize">{review.service_type.replace('-', ' ')}</span>
                          </>
                        )}
                      </div>
                      
                      <div className="flex gap-1 mb-3">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-5 h-5 ${
                              star <= review.rating ? 'text-brand fill-brand' : 'text-neutral-300'
                            }`}
                          />
                        ))}
                      </div>
                      
                      <p className="text-neutral-700">{review.text}</p>
                    </div>
                    
                    <div className="flex gap-2">
                      {activeTab === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => handleApprove(review.id)}
                          className="bg-green-500 hover:bg-green-600 text-white"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(review.id)}
                        className="border-red-300 text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReviews;
